import { z } from 'zod';
import { ExternalRefSchema } from '../external-ref/external-ref.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
/**
 * Ein Angebot an ein Konto (plan 048, nach LIC-34 des License-Branchs).
 *
 * Portiert, nicht nachgebaut: dieselben Felder wie dort, soweit es sie hier
 * schon geben kann. Was mit dem Katalog kommt — `purchaseId`,
 * `preparedLicenseIds`, `documents`, `paymentRoute` — fehlt noch und wird
 * beim Merge ergänzt; `priced` nimmt bis dahin die Positionen der Sales-App.
 *
 * Ein Angebot ist unveränderlich, sobald es verschickt ist; eine Änderung
 * danach ist eine **neue Fassung** (`version`, `supersedes`), die alte bleibt.
 */ /** Entwurf oder verschickt — verschickt friert ein. */ export const OfferDispatchSchema = z.enum([
    'draft',
    'sent'
]);
/** Woher es kommt: vom Vertrieb oder aus dem Self-Service des Shops. */ export const OfferOriginSchema = z.enum([
    'sales',
    'self_service'
]);
export const OfferBillingFrequencySchema = z.enum([
    'monthly',
    'annually'
]);
/** Die Sprache des Dokuments — eine, die des Kunden. */ export const OfferLocaleSchema = z.enum([
    'de',
    'en',
    'fr',
    'hu'
]);
/**
 * Eine Position. Beträge in Minor Units (Cent), wie überall im Backend:
 * das Backend formatiert nie, das Frontend rechnet nie mit Cent als Float.
 */ export const OfferLineSchema = z.object({
    /** Der stabile Schlüssel der Position (die SKU); HubSpot führt ihn als `hs_sku`. */ key: z.string().min(1),
    title: z.string(),
    description: z.string().default(''),
    quantity: z.number(),
    /** Preis einer Einheit je Abrechnungsperiode, Cent. */ unitAmount: z.number().int(),
    /** Menge × Einheit, gerundet, Cent — die Zahl, die jedes System zeigt. */ amount: z.number().int(),
    billingFrequency: OfferBillingFrequencySchema
});
export const OfferPricedSchema = z.object({
    currency: z.literal('EUR'),
    /** Die Zahlweise des Abos; der ISO Check ist jährlich, was auch immer das Abo ist. */ frequency: OfferBillingFrequencySchema,
    lines: z.array(OfferLineSchema),
    /** Summe der Abo-Positionen je Periode, Rabatt eingerechnet, Cent. */ recurringAmount: z.number().int(),
    /** Summe der Positionen je Vertragsjahr unabhängig vom Abo (ISO Check), Cent. */ yearlyAmount: z.number().int(),
    /**
   * Die Konfiguration, aus der die Positionen entstanden sind, in der Form der
   * App, die sie gemacht hat — damit der Kalkulator dort wieder aufgeht.
   */ configuration: z.record(z.string(), z.unknown()).nullable()
});
/** Wer unterschreibt — Name, Bild und Mailadresse, wie unter einer Mail. */ export const OfferSignatureSchema = z.object({
    name: z.string(),
    email: z.email(),
    avatarUrl: z.string().nullable()
});
/**
 * Ein Versand: wann, an wen, wer las mit. Eine Liste, nicht ein Zeitpunkt —
 * `sentAt` sagt, wann das Angebot eingefroren wurde, verschickt wird es
 * womöglich öfter.
 */ export const OfferDeliverySchema = z.object({
    at: z.iso.datetime(),
    to: z.email(),
    cc: z.array(z.email()).default([])
});
/** Was DocuSign zum Umschlag meldet (Connect) oder beim Lesen sagt. */ /**
 * Die Rechnung der Fassung verschicken (plan 048, Schritt 14): der Moco-Entwurf
 * wird endgültig (bekommt die Nummer) und geht per Moco-Mail an den Empfänger.
 */ export const AppAccountOfferInvoiceSendRequestSchema = z.object({
    offerId: ObjectIdSchema,
    to: z.email(),
    subject: z.string().min(1).max(200),
    text: z.string().min(1).max(5000)
});
/**
 * Ein Beleg der Fassung als PDF, wie Moco ihn rendert: das Angebot oder der
 * Rechnungsentwurf — damit der Vertrieb ihn sieht, ohne Moco zu öffnen.
 */ export const AppAccountOfferDocumentRequestSchema = z.object({
    offerId: ObjectIdSchema,
    kind: z.enum([
        'offer',
        'invoice'
    ])
});
export const EnvelopeStatusSchema = z.enum([
    'created',
    'sent',
    'delivered',
    'completed',
    'declined',
    'voided'
]);
export const OfferSchema = z.object({
    _id: z.string(),
    /** Lesbar und fortlaufend; null, solange keine vergeben ist (Moco vergibt sie). */ number: z.string().nullable(),
    /** Die öffentliche, unerratbare Adresse (LIC-34). */ token: z.string(),
    accountId: z.string(),
    tenantId: z.string().nullable(),
    /** Die Fassung je Konto und Deal: 1, 2, 3 … */ version: z.number().int().min(1),
    /** Die Fassung, die diese ersetzt; null bei der ersten. */ supersedes: z.string().nullable(),
    dispatch: OfferDispatchSchema,
    origin: OfferOriginSchema,
    locale: OfferLocaleSchema,
    priced: OfferPricedSchema,
    signature: OfferSignatureSchema.nullable(),
    /** Vertragliche Laufzeit (Malte, 29.10.2026): die Lizenz wird zum Start aktiv und endet zum Ende. */ startsAt: z.iso.datetime().nullable(),
    endsAt: z.iso.datetime().nullable(),
    /** Was Fremdsysteme dazu kennen: der HubSpot-Deal, das Moco-Angebot, der DocuSign-Umschlag. */ externalRefs: z.array(ExternalRefSchema),
    validUntil: z.iso.datetime(),
    sentAt: z.iso.datetime().nullable(),
    deliveries: z.array(OfferDeliverySchema),
    /** Der Stand des Umschlags, wie DocuSign ihn zuletzt meldete; null ohne Umschlag. */ envelopeStatus: EnvelopeStatusSchema.nullable(),
    /** Unterschrieben (Umschlag `completed`): der Vertrag steht. */ acceptedAt: z.iso.datetime().nullable(),
    /** Vom Kunden abgelehnt bzw. von uns storniert — die Fassung ist damit erledigt. */ declinedAt: z.iso.datetime().nullable(),
    /** Was der Kunde beim Ablehnen schrieb (DocuSign); null ohne Angabe. */ declinedReason: z.string().nullable(),
    voidedAt: z.iso.datetime().nullable(),
    createdBy: z.string(),
    createdAt: z.iso.datetime()
});
// ---- Was eine App über `ctx.accounts.offers` darf (plan 048) ----
export const AppAccountOffersListRequestSchema = z.object({
    accountId: ObjectIdSchema
});
export const AppAccountOfferCreateRequestSchema = z.object({
    accountId: ObjectIdSchema,
    locale: OfferLocaleSchema,
    priced: OfferPricedSchema,
    signature: OfferSignatureSchema.nullable(),
    validUntil: z.iso.datetime(),
    startsAt: z.iso.datetime().nullable().default(null),
    endsAt: z.iso.datetime().nullable().default(null),
    /** Was schon bekannt ist, meist der HubSpot-Deal; die Fassung zählt je Deal. */ externalRefs: z.array(ExternalRefSchema).default([]),
    supersedes: ObjectIdSchema.nullable().default(null)
});
/** Der Stammdaten-Abgleich eines Kontos mit HubSpot (LIC-29), bevor ein Angebot die Anschrift einfriert. */ export const AppAccountReconcileRequestSchema = z.object({
    accountId: ObjectIdSchema
});
/** Die Arbeitsbereiche (Tenants) eines Kontos, z. B. der aus der Testphase — der Instanzname im Bestellformular. */ export const AppAccountWorkspacesRequestSchema = z.object({
    accountId: ObjectIdSchema
});
/**
 * Der DocuSign-Umschlag zu einer Fassung (plan 048): Vorlage mit befüllten
 * Feldern, das Moco-PDF der Fassung als Anlage, als Entwurf angelegt. Der
 * Versand ist ein eigener Schritt — nach der Prüfung (Plan 049).
 */ const EnvelopeSignerSchema = z.object({
    name: z.string().min(1),
    email: z.email(),
    /** Die Rolle in der Vorlage; die Vorgabe des Hosts, wenn nicht angegeben. */ role: z.string().min(1).optional()
});
export const AppAccountOfferEnvelopeCreateRequestSchema = z.object({
    offerId: ObjectIdSchema,
    /** Wer unterschreibt: der Ansprechpartner des Kunden. */ signer: EnvelopeSignerSchema,
    /** Wer nach dem Kunden gegenzeichnet (BLIKS IO); ohne Angabe unterschreibt nur der Kunde. */ countersigner: EnvelopeSignerSchema.optional(),
    /** Die DocuSign-Vorlage; ohne sie ein freier Umschlag aus den Anlagen. */ templateId: z.string().min(1).nullable().default(null),
    /** Feldname der Vorlage → Wert, aus der Fassung vorbefüllt. */ fields: z.record(z.string(), z.string()).default({}),
    /** Checkbox der Vorlage → angehakt, z. B. die aktivierten Module (Teil B). */ checkboxes: z.record(z.string(), z.boolean()).default({}),
    /** Felder des Gegenzeichners (BLIKS IO), z. B. der Instanzname aus dem Konto. */ countersignerFields: z.record(z.string(), z.string()).default({}),
    /** Das Moco-Angebot der Fassung als Anlage, wenn die Fassung eines kennt. */ attachMocoOffer: z.boolean().default(true),
    emailSubject: z.string().min(1).max(100)
});
export const AppAccountOfferEnvelopeRequestSchema = z.object({
    offerId: ObjectIdSchema
});
export const AppAccountOfferEnvelopeVoidRequestSchema = z.object({
    offerId: ObjectIdSchema,
    reason: z.string().min(1).max(200)
});
export const AppAccountOfferRememberRefRequestSchema = z.object({
    offerId: ObjectIdSchema,
    ref: ExternalRefSchema,
    /** Die Nummer, die das Fremdsystem dem Angebot gab (Moco: A26xxxx); setzt `number`, wenn angegeben. */ number: z.string().min(1).optional()
});

//# sourceMappingURL=offer.schema.js.map